import { useEffect } from 'react';
import { create } from 'zustand';

import { FORGE_ENABLED } from '../lib/runtimeFlags';
import forgeDashboardService from '../services/forgeDashboardService';

interface ForgeApiState {
  /** Global Forge API kill-switch; null until the backend has answered. */
  enabled: boolean | null;
  load: () => Promise<void>;
  setEnabled: (enabled: boolean) => void;
}

let inflight: Promise<void> | null = null;

/**
 * Shared state of the super-admin Forge API switch, so the sidebar, the use case
 * page and the Forge dashboard react immediately when it is toggled.
 */
export const useForgeApiStore = create<ForgeApiState>()((set, get) => ({
  // A build without Forge never asks the backend and stays off.
  enabled: FORGE_ENABLED ? null : false,

  load: () => {
    if (!FORGE_ENABLED || get().enabled !== null) return Promise.resolve();
    if (!inflight) {
      inflight = forgeDashboardService
        .getApiSwitch()
        .then(({ enabled }) => set({ enabled }))
        // Fall back to "on" so a failed status read never hides Forge features.
        .catch(() => set({ enabled: true }))
        .finally(() => {
          inflight = null;
        });
    }
    return inflight;
  },

  setEnabled: (enabled) => set({ enabled }),
}));

/** Loads the switch on first use and returns its current value (null while loading). */
export function useForgeApiEnabled(): boolean | null {
  const enabled = useForgeApiStore((s) => s.enabled);
  const load = useForgeApiStore((s) => s.load);
  useEffect(() => {
    if (enabled === null) void load();
  }, [enabled, load]);
  return enabled;
}
